import {
  Card,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { unwrapResult } from '@reduxjs/toolkit';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { FC, useEffect, useMemo, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSnackBar } from 'src/hooks';
import { slices, useAppDispatch } from 'src/redux';
import { CustomKeyboardEvent } from 'src/types';
import { Supplier } from 'src/types/supplier';
import { formatCurrency } from 'src/utils';
import { AlternatingColorTableRow } from 'src/components';
import { PurchaseOrderListItem } from 'src/redux/slices/purchase-order/types';
import { TaxCertificatePurchaseOrder } from 'src/redux/slices/purchase-order/tax-certificate/types';

const { actions: purchaseOrderActions } = slices.purchaseOrder;

interface Props {
  supplier: Supplier | null;
  selectedPurchaseOrders: PurchaseOrderListItem[];
  setSelectedPurchaseOrders: (payload: PurchaseOrderListItem[]) => void;
  getGrandTotal?: (grandTotal: number) => void;
  isCreatePage?: boolean;
  existingPurchaseOrders: TaxCertificatePurchaseOrder[];
  modifyExistingPurchaseOrders: React.Dispatch<
    React.SetStateAction<TaxCertificatePurchaseOrder[]>
  >;
  deletedExistingPO: TaxCertificatePurchaseOrder[];
  setDeletedExistingPO: React.Dispatch<
    React.SetStateAction<TaxCertificatePurchaseOrder[]>
  >;
}

export const TaxPoSection: FC<Props> = ({
  supplier,
  selectedPurchaseOrders,
  setSelectedPurchaseOrders,
  getGrandTotal,
  existingPurchaseOrders,
  isCreatePage,
  modifyExistingPurchaseOrders,
  deletedExistingPO,
  setDeletedExistingPO
}) => {
  const snackbar = useSnackBar();
  const dispatch = useAppDispatch();
  const [poInputVal, setPoInputVal] = useState<string>('');
  const [isPoInputLoading, setIsPoInputLoading] = useState<boolean>(false);

  // coded this way cause po.amount is string and causes type error
  const grandTotal = useMemo(() => {
    const total = selectedPurchaseOrders.reduce((sum, po) => {
      // Remove anything that's not a digit or decimal
      const numeric = po?.amount?.replace(/[^\d.-]/g, '') || '0';
      return sum + parseFloat(numeric);
    }, 0);

    const existingTotal = !isCreatePage
      ? existingPurchaseOrders.reduce((sum, po) => sum + (po?.amount ?? 0), 0)
      : 0;

    const newTotal = total + existingTotal;

    // Round to 2 decimal places
    return Math.round((newTotal + Number.EPSILON) * 100) / 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPurchaseOrders, existingPurchaseOrders]);

  const onAddNewPoNos = async () => {
    if (!supplier?.id || poInputVal === '') return;
    setIsPoInputLoading(true);

    const newInput = poInputVal.trim();
    if (
      selectedPurchaseOrders?.some(
        (po: PurchaseOrderListItem) => po.order_no === newInput
      )
    ) {
      snackbar.show({
        severity: 'error',
        message: 'PO number already added.'
      });
      setIsPoInputLoading(false);
      return;
    }

    const res = unwrapResult(
      await dispatch(
        purchaseOrderActions.getPurchaseOrdersThunk({
          po_no: poInputVal,
          supplier: supplier?.id,
          action: 'tax_certificate'
        })
      )
    );

    const { success, message, errors, data } = res.originalData;

    // do not accept if still loading
    if (success) {
      const poOptions = data ?? [];
      const matchedOption = poOptions.find((p) => p.order_no === newInput);

      if (matchedOption) {
        setSelectedPurchaseOrders([...selectedPurchaseOrders, matchedOption]);
        setPoInputVal('');

        snackbar.show({
          severity: 'success',
          message: 'PO added successfully'
        });
      } else {
        snackbar.show({
          severity: 'error',
          message: 'PO number not found. Please check for typos.'
        });
      }
    } else {
      snackbar.show({
        severity: 'error',
        message: errors
          ? Object.values(errors).join(', ')
          : message ?? 'Failed to add new po no. Please contact devs'
      });
    }

    setIsPoInputLoading(false);
  };

  const onKeyDown = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      if (!isCreatePage && deletedExistingPO.length) {
        const newInput = poInputVal.trim();

        setDeletedExistingPO((prevDeleted) => {
          const itemToMove = prevDeleted.find((po) => po.po_no === newInput);
          if (!itemToMove) return prevDeleted;

          modifyExistingPurchaseOrders((prevExisting) => [
            ...prevExisting,
            itemToMove
          ]);

          setPoInputVal('');

          snackbar.show({
            severity: 'success',
            message: 'PO added successfully'
          });

          return prevDeleted.filter((po) => po.po_no !== newInput);
        });
      } else {
        onAddNewPoNos();
      }
    }
  };

  const onDeletePo = (deleteId?: number) => {
    if (!deleteId) return;
    const newPOs = selectedPurchaseOrders.filter((po) => po.po_id !== deleteId);
    setSelectedPurchaseOrders(newPOs);
  };

  const onDeleteInitialPo = (deleteId?: number) => {
    if (!deleteId) return;

    modifyExistingPurchaseOrders((prevExisting) => {
      const itemToMove = prevExisting.find((po) => po.po_id === deleteId);
      if (!itemToMove) return prevExisting;

      setDeletedExistingPO((prevDeleted) => [...prevDeleted, itemToMove]);

      return prevExisting.filter((po) => po.po_id !== deleteId);
    });
  };

  useEffect(() => {
    if (getGrandTotal) {
      getGrandTotal(grandTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grandTotal]);

  return (
    <>
      <TextField
        disabled={isPoInputLoading || !supplier?.id}
        fullWidth
        value={poInputVal}
        onChange={(e) => setPoInputVal(e.target.value ?? '')}
        variant="outlined"
        label="Add PO No."
        onKeyDown={onKeyDown}
        helperText={!supplier?.id ? 'Choose supplier first' : ''}
        style={{ width: '20em' }}
        InputProps={{
          endAdornment: (
            <>
              {isPoInputLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )}
            </>
          )
        }}
      />
      <Card>
        <PerfectScrollbar style={{ maxHeight: '50vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Supplier Name</TableCell>
                <TableCell>PO No.</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isCreatePage && existingPurchaseOrders
                ? existingPurchaseOrders?.map((po) => (
                    <AlternatingColorTableRow key={po?.po_id}>
                      <TableCell>{supplier?.name ?? ''}</TableCell>
                      <TableCell>{po?.po_no ?? ''}</TableCell>
                      <TableCell>{formatCurrency(po?.amount ?? 0)}</TableCell>
                      <TableCell>
                        <DeleteIcon
                          color="secondary"
                          onClick={() => onDeleteInitialPo(po?.po_id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                    </AlternatingColorTableRow>
                  ))
                : null}
              {selectedPurchaseOrders?.map((po, ind) => (
                <AlternatingColorTableRow key={ind}>
                  <TableCell>{supplier?.name ?? ''}</TableCell>
                  <TableCell>{po.order_no ?? ''}</TableCell>
                  <TableCell>{po.amount ?? ''}</TableCell>
                  <TableCell>
                    <DeleteIcon
                      color="secondary"
                      onClick={() => onDeletePo(po?.po_id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                </AlternatingColorTableRow>
              ))}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Typography variant="h4">
          {`Grand Total: ${formatCurrency(grandTotal)}`}
        </Typography>
      </div>
    </>
  );
};
